<template>
	<div class="parcels-card" :class="{ compact, peeked }">
		<div class="header" ref="headerEl">
			<div class="header-top">
				<div class="title-group">
					<h3 class="title">{{ t("parcels.selectedParcels") }} ({{ items.length }})</h3>
					<span v-if="peeked" class="peeked-total-area">{{ formatArea(totalAreaMeters) }}</span>
				</div>
				<div class="header-actions">
					<button
						v-if="!compact"
						type="button"
						class="icon-button collapse-btn"
						:title="$t('callToActions.collapsePanel')"
						@click="emit('collapse')"
					>
						<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
					</button>
					<button
						type="button"
						class="icon-button collapse-btn"
						:title="$t('callToActions.close')"
						@click="emit('close')"
					>
						<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
					</button>
				</div>
			</div>
		</div>

		<div class="separator"></div>

		<div v-if="peeked" ref="itemsEl" class="compact-lot-list">
			<span v-for="parcel in items" :key="parcel.parcel_id" class="chip">#{{ parcel.parcel_id }}</span>
		</div>
		<div v-else ref="itemsEl" class="parcel-list" :class="{ 'two-columns': compact && expanded }">
			<div class="parcel-box" v-for="(parcel, index) in items" :key="parcel.parcel_id">
				<div class="parcel-info">
					<div class="parcel-id-row copy-group">
						<span class="parcel-id">#{{ parcel.parcel_id }}</span>
						<CopyButton :text="String(parcel.parcel_id)" :title="$t('callToActions.copyLotNumber')" />
						<!-- label the first parcel as the primary parcel when in a project -->
						<span v-if="index === 0 && currentProject != null && canCreateQuickOpportunity" class="badge">
							{{ $t("parcels.primary") }}
						</span>
					</div>
					<div class="parcel-area copy-group">
						<span>{{ formatArea(parcel.area_square_meters) }}</span>
						<CopyButton :text="formatArea(parcel.area_square_meters)" :title="$t('callToActions.copyArea')" size="md" />
					</div>
				</div>
				<div class="parcel-actions">
					<LinksIcons :links="mapsLinksFor(String(parcel.parcel_id))" />
				</div>
			</div>
		</div>

		<template v-if="!peeked">
			<div class="separator"></div>

			<div class="stat-tiles">
				<div class="tile">
					<span class="label">{{ t("parcels.totalArea") }}</span>
					<div class="value-group">
						<span class="value">{{ formatArea(totalAreaMeters) }}</span>
						<CopyButton :text="formatArea(totalAreaMeters)" :title="$t('callToActions.copyTotalArea')" />
					</div>
				</div>
			</div>

			<div
				v-if="
					(store.isUserLoggedIn && store.currentUser.has(Permission.ParcelExport)) ||
					showCreateTargetButton ||
					hasTransactionSection ||
					comparableAnalysisStore.canStart
				"
				class="actions-box"
			>
				<span class="label">{{ $t("parcelCard.accordions.actions") }}</span>
				<div class="actions-content">
					<div class="primary-actions">
						<div v-if="showCreateTargetButton" class="action-slot target">
							<Button
								type="button"
								variant="target"
								size="m"
								style="width: 100%; min-width: 0"
								:disabled="isQuickOpportunityLoading"
								@click="handleQuickOpportunity"
							>
								<span v-if="isQuickOpportunityLoading" class="loader"></span>
								<span v-else>{{ $t("callToActions.createOpportunity") }}</span>
							</Button>
						</div>

						<div v-if="store.isUserLoggedIn && store.currentUser.has(Permission.ParcelExport)" class="action-slot">
							<ParcelExportDropdown
								size="m"
								:busy="isExportingParcelSelection"
								:busy-label="
									t(isSingleParcelSelection ? 'callToActions.exportingParcel' : 'callToActions.exportingParcels')
								"
								:shapefile-description="
									t(
										isSingleParcelSelection
											? 'callToActions.shapefileExportDescription'
											: 'callToActions.dissolvedShapefileExportDescription'
									)
								"
								:kml-description="
									t(
										isSingleParcelSelection
											? 'callToActions.kmlExportDescription'
											: 'callToActions.dissolvedKmlExportDescription'
									)
								"
								@select="downloadParcelSelection"
							/>
						</div>
						<div v-if="hasTransactionSection || comparableAnalysisStore.canStart" class="action-slot">
							<Menu same-width :gutter="2" @select="onCreateMenuSelect">
								<template #trigger>
									<Button type="button" variant="secondary" size="m" class="action-button">
										<AppIcon :name="IconName.Plus" class="action-icon" :size="16" :stroke-width="1.75" />
										{{ $t("callToActions.create") }}
										<AppIcon :name="IconName.ChevronDown" class="menu-trigger-chevron" :size="10" :stroke-width="2" />
									</Button>
								</template>
								<MenuItem v-if="hasTransactionSection" :value="CreateAction.Transaction" class="create-menu-item">
									<span class="format">{{ $t("callToActions.createTransactionItem") }}</span>
									<span class="description">{{ $t("callToActions.createTransactionDescription") }}</span>
								</MenuItem>
								<MenuItem
									v-if="comparableAnalysisStore.canStart"
									:value="CreateAction.ComparableAnalysis"
									class="create-menu-item"
								>
									<span class="format">{{ $t("callToActions.createComparableAnalysis") }}</span>
									<span class="description">{{ $t("callToActions.createComparableAnalysisDescription") }}</span>
								</MenuItem>
							</Menu>
						</div>
					</div>

					<section v-if="showTransactionForm" ref="transactionSectionEl" class="transaction-section">
						<form
							class="transaction-form"
							@submit.prevent="createTransactionEnabled && handleCreateTransaction(inscriptionNumber, items)"
						>
							<div class="input-wrapper">
								<input
									ref="transactionInputEl"
									type="text"
									class="input-transaction"
									name="inscriptionNumber"
									v-model="inscriptionNumber"
									:placeholder="$t('transaction.inscription')"
									pattern="\s*\d{2}\s*\d{3}\s*\d{3}\s*"
									required
									@input="
										event => (createTransactionEnabled = (event.currentTarget as HTMLInputElement)?.validity.valid)
									"
								/>
							</div>
							<Button
								type="submit"
								variant="tertiary"
								size="m"
								class="create-transaction-btn"
								:disabled="!createTransactionEnabled"
							>
								<span class="label-full">{{ $t("callToActions.createTransaction") }}</span>
								<span class="label-short">{{ $t("callToActions.createTransactionShort") }}</span>
							</Button>
						</form>
					</section>
				</div>
			</div>

			<div class="separator"></div>

			<div v-if="store.currentUser?.has(Permission.DevPotentialRead)" class="accordions">
				<AccordionRoot :multiple="true" :default-value="[]" persistence-key="parcels-card-accordion">
					<AccordionItem value="prospection" title="Prospection">
						<div v-if="store.currentUser?.has(Permission.DevPotentialWrite)" class="dev-potential-section">
							<ArkHoverCard :open-delay="200" :close-delay="100">
								<template #trigger>
									<label for="dev-potential-levels" class="has-tooltip">{{ $t("development-potential.title") }}</label>
								</template>
								<dl class="shortcuts-tooltip">
									<div class="shortcut">
										<dt>q</dt>
										<dd>{{ $t("development-potential.high") }}</dd>
									</div>
									<div class="shortcut">
										<dt>w</dt>
										<dd>{{ $t("development-potential.medium") }}</dd>
									</div>
									<div class="shortcut">
										<dt>e</dt>
										<dd>{{ $t("development-potential.low") }}</dd>
									</div>
									<div class="shortcut">
										<dt>z</dt>
										<dd>{{ $t("development-potential.none") }}</dd>
									</div>
									<div class="shortcut">
										<dt>x</dt>
										<dd>{{ $t("development-potential.unspecified") }}</dd>
									</div>
								</dl>
							</ArkHoverCard>
							<ArkListboxDropdown
								id="dev-potential-levels"
								v-model="devPotentialCode"
								:options="transformedDevPotentialLevels"
								:clearable="false"
								:placeholder="selectedParcels.length === 1 ? '' : $t('development-potential.mixed')"
								:multiple="false"
								style="flex: 0 0 120px; width: 120px"
							/>
						</div>
						<div v-else class="detail">
							<span class="label">{{ $t("development-potential.title") }}</span>
							<span class="value">{{ devPotentialLabel }}</span>
						</div>
					</AccordionItem>
				</AccordionRoot>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, inject, watchEffect, nextTick, watch } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { storeToRefs } from "pinia"
import { onClickOutside, useDebounceFn, useElementSize } from "@vueuse/core"
import Menu from "@/components/ui/Menu.vue"
import MenuItem from "@/components/ui/MenuItem.vue"
import { useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
import Button from "@/components/ui/Button.vue"
import { setProspectPotential, getParcel, createOpportunity } from "@/utils/ApiClient"
import type { ParcelExportFormat } from "@/utils/ApiClient"
import { UseRootStore, Permission } from "@/model/RootStore"
import { ProjectType } from "@/graphql"
import { useI18n } from "vue-i18n"
import { formatQuantity } from "@/utils/format"
import { ProjectStoreKey } from "@/stores/projectStore.ts"
import { onMounted, onBeforeUnmount } from "vue"
import { DevelopmentPotential } from "@/model/DataModel.ts"
import { useProspectStore } from "@/stores/prospectStore.ts"
import { useOpportunityStore } from "@/stores/opportunityStore"
import { useToastStore } from "@/stores/toastStore.ts"
import * as download from "@/utils/download"
import ParcelExportDropdown from "./ParcelExportDropdown.vue"
import CopyButton from "@/components/ui/CopyButton.vue"
import LinksIcons, { type EntityLink } from "@/components/ui/LinksIcons.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AreaUnit } from "@/utils/unitSystem"

import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import ArkListboxDropdown from "../ui/ArkListboxDropdown.vue"
import ArkHoverCard from "../ui/ArkHoverCard.vue"
import { config, Feature } from "@/AppConfig"
import { isHuntingMode } from "@/stores/projectStore.ts"

const { t } = useI18n()
const { areaUnitSymbol, convertAreaToPreferredUnit } = useUnitSystem()

function formatArea(value: unknown): string {
	const numberValue = Number(value)

	if (value === null || value === undefined || value === "" || !Number.isFinite(numberValue)) {
		return t("opportunities.unknown")
	}

	const convertedArea = convertAreaToPreferredUnit(numberValue, AreaUnit.SquareMeters)

	return `${formatQuantity(Math.round(convertedArea))} ${areaUnitSymbol.value}`
}

const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)
const isHunting = computed(() => isHuntingMode())

const props = withDefaults(
	defineProps<{
		items: Record<string, any>[]
		canCreateQuickOpportunity?: boolean
		canCreateDetailedOpportunity?: boolean
		mapRef?: any
		compact?: boolean
		expanded?: boolean
	}>(),
	{
		canCreateQuickOpportunity: false,
		canCreateDetailedOpportunity: false,
	}
)

const emit = defineEmits<{
	(e: "collapse"): void
	(e: "close"): void
	(e: "header-resize", height: number): void
}>()

const peeked = computed(() => props.compact && !props.expanded)

// when peeked, the peek must extend past the lot list's own height, not just stop at its offsetTop
const headerEl = ref<HTMLElement | null>(null)
const itemsEl = ref<HTMLElement | null>(null)
const { height: headerHeight } = useElementSize(headerEl)
const { height: itemsHeight } = useElementSize(itemsEl, undefined, { box: "border-box" })
watch(
	[headerHeight, itemsHeight, peeked],
	() => {
		const peekHeight = peeked.value
			? (itemsEl.value?.offsetTop ?? 0) + itemsHeight.value
			: itemsEl.value?.offsetTop ?? headerHeight.value
		if (peekHeight > 0) emit("header-resize", peekHeight)
	},
	{ immediate: true }
)

// only shown when a project is selected while hunting
const showCreateTargetButton = computed(
	() => props.canCreateQuickOpportunity && currentProject.value != null && isHunting.value
)

const toastStore = useToastStore()
const isQuickOpportunityLoading = ref(false)
const selectedParcels = computed(() => props.items)
const isSingleParcelSelection = computed(() => selectedParcels.value.length === 1)

const inscriptionNumber = ref<string>("")
const createTransactionEnabled = ref(false)

const showTransactionForm = ref(false)
const transactionInputEl = ref<HTMLInputElement | null>(null)
const transactionSectionEl = ref<HTMLElement | null>(null)

async function openTransactionForm(): Promise<void> {
	showTransactionForm.value = true
	await nextTick()
	transactionInputEl.value?.focus()
}

enum CreateAction {
	Transaction = "transaction",
	ComparableAnalysis = "comparableAnalysis",
}

const hasTransactionSection = computed(
	() => store.currentUser?.has(Permission.TransactionWrite) && currentProject.value?.type !== ProjectType.Cca
)

function onCreateMenuSelect(details: { value: string }): void {
	if (details.value === CreateAction.Transaction) {
		openTransactionForm()
		return
	}
	const parcelIds = props.items.map(item => String(item.parcel_id))
	comparableAnalysisStore.start(parcelIds, t("comparableAnalysis.defaultName", { parcels: parcelIds.join(", ") }))
}

onClickOutside(transactionSectionEl, () => {
	showTransactionForm.value = false
})

const coords = ref<Record<string, { latitude?: string; longitude?: string }>>({})
async function loadParcelCoordinates() {
	for (const parcel of props.items) {
		const parcelId = parcel.parcel_id.toString()
		if (parcelId in coords.value || !/^\d+$/.test(parcelId)) continue
		try {
			const parcel = await getParcel(parcelId)
			if (parcel) {
				coords.value[parcelId] = {
					latitude: parcel.location.coordinates.latitude,
					longitude: parcel.location.coordinates.longitude,
				}
			} else {
				coords.value[parcelId] = {}
			}
		} catch (error) {
			console.error(`Error loading parcel ${parcelId}`, error)
			coords.value[parcelId] = {}
		}
	}
}
watchEffect(() => {
	loadParcelCoordinates()
})

function mapsLinksFor(parcelId: string): EntityLink[] {
	const parcelCoords = coords.value[parcelId]
	if (!parcelCoords?.latitude || !parcelCoords?.longitude) return []
	return [
		{
			id: "maps",
			label: t("callToActions.openInMaps"),
			icon: "map-pin",
			href: `https://www.google.com/maps/search/?api=1&query=${parcelCoords.latitude},${parcelCoords.longitude}`,
			target: "_blank",
		},
	]
}

const store = UseRootStore()
const comparableAnalysisStore = useComparableAnalysisStore()
const opportunityStore = useOpportunityStore()

const totalArea = computed(() => {
	return props.items.reduce((acc, item) => acc + Number(item.area_square_feet || 0), 0)
})

const totalAreaMeters = computed(() => {
	return props.items.reduce((acc, item) => acc + Number(item.area_square_meters || 0), 0)
})

const handleQuickOpportunity = async () => {
	try {
		isQuickOpportunityLoading.value = true
		const projectId = currentProject.value?.id
		if (!projectId) {
			toastStore.error("opportunity-creation.messages.error")
			return
		}

		const opportunityId = await createOpportunity(store.userApiToken, {
			parcelIDs: props.items.map(item => item.parcel_id),
			totalAreaSqft: totalArea.value,
			marketingType: "internal",
			projectId,
			...(currentProject.value?.type !== ProjectType.Workspace
				? { ccaContractHubspotId: currentProject.value?.ccaContractHubspotId }
				: {}),
		})

		if (opportunityId == null) {
			toastStore.error("opportunity-creation.messages.error")
			isQuickOpportunityLoading.value = false
			return
		}

		props.mapRef?.registerPendingTargetSync?.(opportunityId)
		opportunityStore.invalidateProjectOpportunities(projectId, { selectOpportunityId: opportunityId })
		toastStore.success("opportunity-creation.messages.success")
	} catch (error) {
		console.error("Error creating opportunity:", error)
		toastStore.error("opportunity-creation.messages.error")
	} finally {
		isQuickOpportunityLoading.value = false
	}
}

const isExportingParcelSelection = ref(false)

async function downloadParcelSelection(format: ParcelExportFormat) {
	isExportingParcelSelection.value = true
	try {
		const parcelIds = selectedParcels.value.map(parcel => String(parcel.parcel_id))
		const [data, filename] = await store.getParcelSelectionExport(parcelIds, format)
		download.push(data, filename)
	} catch (error) {
		console.error(`Selected parcels ${format.toUpperCase()} download failed:`, error)
	} finally {
		isExportingParcelSelection.value = false
	}
}

async function handleCreateTransaction(inscriptionNumber: string, parcels: Record<string, any>[]) {
	const parcelIds = parcels.map(p => p["parcel_id"])

	try {
		const transaction = await store.createTransaction(inscriptionNumber, parcelIds)
		store.addSelectedTransaction(transaction)
	} catch (error) {
		console.warn("Transaction creation failed:", error)
		toastStore.error("notifications.defaultError")
	}
}

//  Development Potential

const prospectStore = useProspectStore()

watch(
	selectedParcels,
	async newParcels => {
		if (!store.isUserLoggedIn || !store.currentUser.has(Permission.DevPotentialRead)) return

		for (const parcel of newParcels) {
			const parcelId = parcel.parcel_id.toString()

			const existing = prospectStore.prospects.find(p => p.prospectID === parcelId)
			if (!existing) {
				await prospectStore.loadProspect(store.userApiToken, parcelId)
			}
		}
	},
	{ immediate: true }
)

const devPotential = computed({
	get() {
		const potentials = selectedParcels.value.map(parcel => {
			const prospect = prospectStore.prospects.find(p => p.prospectID === parcel.parcel_id.toString())
			return prospect?.potential
		})

		const uniquePotentials = [...new Set(potentials)]

		if (selectedParcels.value.length === 1) {
			const currentPotential = uniquePotentials[0]
			const foundOption = devPotentialLevels.value.find(opt => opt.code === currentPotential)
			return foundOption || null
		} else if (uniquePotentials.length === 1) {
			return devPotentialLevels.value.find(opt => opt.code === uniquePotentials[0]) || null
		}

		return null
	},
	set(newValue) {
		if (newValue) {
			updateDevPotential(newValue.code)
		}
	},
})

const devPotentialLevels = computed(() => [
	{ label: t("development-potential.high"), code: DevelopmentPotential.High },
	{ label: t("development-potential.medium"), code: DevelopmentPotential.Medium },
	{ label: t("development-potential.low"), code: DevelopmentPotential.Low },
	{ label: t("development-potential.none"), code: DevelopmentPotential.None },
	{ label: t("development-potential.unspecified"), code: DevelopmentPotential.Unspecified },
])

const transformedDevPotentialLevels = computed(() => {
	return devPotentialLevels.value.map(level => ({
		value: level.code,
		label: level.label,
		disabled: false,
	}))
})

const devPotentialLabel = computed(
	() =>
		devPotential.value?.label ??
		(selectedParcels.value.length > 1 ? t("development-potential.mixed") : t("opportunities.unknown"))
)

const devPotentialCode = computed({
	get: () => devPotential.value?.code ?? null,
	set: (code: DevelopmentPotential | null) => {
		if (code === null) {
			devPotential.value = null
		} else {
			const foundOption = devPotentialLevels.value.find(opt => opt.code === code)
			if (foundOption) {
				devPotential.value = foundOption
			}
		}
	},
})

async function updateDevPotential(potential: DevelopmentPotential) {
	const parcelIDs = selectedParcels.value.map(item => item.parcel_id)
	const previousPotentials = new Map<string, DevelopmentPotential | undefined>()

	if (!store.visibleLayers.includes("prospects")) {
		store.setVisibleLayers([...store.visibleLayers, "prospects"])
	}

	for (const parcelId of parcelIDs) {
		const id = parcelId.toString()
		const prospect = prospectStore.prospects.find(p => p.prospectID === id)
		previousPotentials.set(id, prospect?.potential)
		prospectStore.updateByParcelId(id, { potential })
	}

	const updates = await Promise.all(
		parcelIDs.map(async parcelId => {
			const success = await setProspectPotential(store.userApiToken, parcelId, potential)

			return {
				parcelId: parcelId.toString(),
				success,
			}
		})
	)
	const failedUpdates = updates.filter(update => !update.success)

	for (const update of failedUpdates) {
		const previousPotential = previousPotentials.get(update.parcelId) ?? DevelopmentPotential.Unspecified
		prospectStore.updateByParcelId(update.parcelId, { potential: previousPotential })
	}

	props.mapRef?.refreshProspectsLayer?.()

	if (failedUpdates.length > 0) {
		console.error(
			`Failed to update development potential for parcels: ${failedUpdates.map(update => update.parcelId).join(", ")}`
		)
		toastStore.error("development-potential-messages.error")

		return false
	}

	toastStore.success("development-potential-messages.success")

	return true
}

// Debounce for hotkeys so we will only send one request if user presses keys quickly
const debouncedUpdateDevPotential = useDebounceFn(
	(potential: DevelopmentPotential) => updateDevPotential(potential),
	500
)

async function handleKeyboardShortcuts(event: KeyboardEvent) {
	const activeElement = document.activeElement
	if (
		activeElement?.tagName === "INPUT" ||
		activeElement?.tagName === "TEXTAREA" ||
		activeElement?.tagName === "SELECT"
	) {
		return
	}

	if (
		!itemsEl.value ||
		!store.isUserLoggedIn ||
		!store.currentUser.has(Permission.DevPotentialWrite) ||
		selectedParcels.value.length === 0
	)
		return

	let potential: DevelopmentPotential | undefined
	switch (event.key) {
		case "q":
			potential = DevelopmentPotential.High
			break
		case "w":
			potential = DevelopmentPotential.Medium
			break
		case "e":
			potential = DevelopmentPotential.Low
			break
		case "z":
			potential = DevelopmentPotential.None
			break
		case "x":
			potential = DevelopmentPotential.Unspecified
			break
	}
	if (potential) {
		event.preventDefault()
		debouncedUpdateDevPotential(potential)
	}
}

onMounted(() => {
	document.addEventListener("keyup", handleKeyboardShortcuts)
})
onBeforeUnmount(() => {
	document.removeEventListener("keyup", handleKeyboardShortcuts)
})

// End of Development Potential

// scroll to bottom when new items are added
const previousItemCount = ref(props.items.length)

watchEffect(async () => {
	if (props.items.length > previousItemCount.value) {
		await nextTick()
		if (itemsEl.value) {
			itemsEl.value.scrollTop = itemsEl.value.scrollHeight
		}
	}
	previousItemCount.value = props.items.length
})
</script>

<style scoped>
.parcels-card {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	padding: var(--spacing-md) var(--spacing-lg);
	overflow: auto;
	background-color: var(--neutral-0);

	.header {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		.header-top {
			display: flex;
			justify-content: space-between;
			align-items: end;
			gap: var(--spacing-sm);
		}

		.header-actions {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			flex-shrink: 0;
		}

		.title-group {
			display: flex;
			align-items: baseline;
			gap: var(--spacing-sm);
			min-width: 0;
		}

		.title {
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-semibold);
			line-height: var(--line-height-tight);
		}

		.peeked-total-area {
			flex-shrink: 0;
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-medium);
			white-space: nowrap;
		}
	}

	&.peeked .header .title {
		font-size: var(--font-size-base);
	}

	.icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		padding: 0;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		background: var(--neutral-0);
		color: var(--neutral-600);
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			color: var(--primary-green);
			border-color: var(--primary-green);
		}

		&.collapse-btn {
			border-color: transparent;
		}
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		background-color: var(--neutral-400);
	}

	.stat-tiles {
		display: grid;
		grid-template-columns: 1fr;
		gap: var(--spacing-sm);

		.tile {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			padding: var(--spacing-sm) var(--spacing-md);
			border-radius: var(--border-radius-lg);
			background: var(--neutral-50);

			.label {
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-semibold);
				text-transform: uppercase;
				letter-spacing: 0.02em;
			}

			.value-group {
				display: flex;
				align-items: center;
				gap: var(--spacing-xxs);
			}

			.value {
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-medium);
				font-weight: var(--font-weight-semibold);
				line-height: var(--line-height-tight);
			}
		}
	}

	.actions-box {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-md);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-50);

		> .label {
			color: var(--neutral-600);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}
	}

	.actions-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.primary-actions {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);

		.action-slot {
			flex: 1;
			min-width: 90px;
			height: 28px;

			.action-button,
			:deep(.trigger) {
				width: 100%;
				height: 100%;
				min-width: 0;
			}

			.action-icon {
				flex-shrink: 0;
				margin-right: var(--spacing-xxs);
			}

			.menu-trigger-chevron {
				flex-shrink: 0;
				margin-left: var(--spacing-xs);
			}

			&.target {
				flex-basis: 100%;
			}
		}
	}

	&.compact .primary-actions {
		flex-direction: column;

		.action-slot {
			width: 100%;
		}
	}

	.create-menu-item {
		display: flex;
		flex-direction: column;
		gap: 2px;
		white-space: normal;

		.format {
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
		}

		.description {
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			line-height: var(--line-height-normal);
		}
	}

	.transaction-section {
		container-type: inline-size;
		container-name: transaction-section;

		.transaction-form {
			display: grid;
			grid-template-columns: 1fr auto;
			column-gap: var(--spacing-xs);
			align-items: stretch;
			height: 28px;
		}

		.input-wrapper {
			min-width: 0;
			overflow: hidden;
		}

		.create-transaction-btn {
			min-width: max-content;
			height: 100%;

			.label-short {
				display: none;
			}
		}

		@container transaction-section (max-width: 280px) {
			.transaction-form .create-transaction-btn {
				.label-full {
					display: none;
				}

				.label-short {
					display: inline;
				}
			}
		}

		.input-transaction {
			width: 100%;
			height: 100%;
			padding: 0 var(--spacing-sm) 0 var(--space-10);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-base);
			text-align: center;
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			transition: border-color 0.2s ease;

			&:hover:not(:disabled) {
				border-color: var(--neutral-600);
			}

			&:focus,
			&:focus-visible {
				outline: none;
				border-color: var(--neutral-600);
				box-shadow: var(--shadow-sm);
			}

			&:invalid:not(:empty) {
				border-color: var(--accent-warning);
			}
		}
	}

	.compact-lot-list {
		display: flex;
		flex-wrap: wrap;
		align-content: flex-start;
		gap: var(--spacing-xs);
		max-height: 80px;
		overflow-y: auto;
		scroll-behavior: smooth;
		padding-bottom: var(--spacing-md);

		.chip {
			display: inline-flex;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border: var(--border-width-base) solid var(--neutral-100);
			border-radius: var(--border-radius-xl);
			background-color: var(--neutral-100);
			color: var(--neutral-700);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
			box-shadow: var(--shadow-sm);
		}
	}

	.parcel-list {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		max-height: 500px;
		overflow-y: auto;
		scroll-behavior: smooth;

		&.two-columns {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			max-height: 240px;

			.parcel-box {
				flex-direction: row;
				align-items: center;
				gap: var(--spacing-xxs);
				padding: var(--spacing-xs) var(--spacing-sm);

				.parcel-id {
					font-size: var(--font-size-small);
				}

				.parcel-area {
					font-size: var(--font-size-xxs);
				}

				.parcel-actions :deep(.icon-button) {
					width: 24px;
					height: 24px;
				}
			}
		}
	}

	.parcel-box {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-0);
		box-shadow: var(--shadow-sm);
		transition: border-color 0.2s ease;

		&:hover {
			border-color: var(--neutral-400);
		}

		.parcel-info {
			display: flex;
			flex: 1 1 auto;
			flex-direction: column;
			gap: var(--spacing-xxs);
			min-width: 0;
			overflow: hidden;
			font-family: var(--font-family-primary);
		}

		.parcel-id-row {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
		}

		.parcel-id {
			flex-shrink: 1;
			min-width: 0;
			font-size: var(--font-size-medium);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.badge {
			display: inline-flex;
			flex-shrink: 0;
			align-items: center;
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background-color: var(--primary-green);
			color: var(--neutral-0);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
		}

		.parcel-area {
			display: flex;
			align-items: center;
			color: var(--neutral-600);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-regular);
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.parcel-actions {
			display: flex;
			flex-direction: column;
			flex-shrink: 0;
			gap: var(--spacing-xxs);
		}
	}

	.accordions {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);

		:deep(.accordion-content) {
			padding: var(--spacing-sm) var(--spacing-md) var(--spacing-md);
		}

		.dev-potential-section {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);
			padding: 6px 0;
			border-bottom: var(--border-width-thin) solid var(--neutral-200);

			&:last-child {
				border-bottom: none;
			}

			label {
				flex: 1 1 auto;
				min-width: 0;
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-medium);

				&.has-tooltip {
					cursor: help;
				}
			}

			:deep(.ark-listbox-dropdown) {
				flex: 0 0 auto;

				.trigger {
					height: var(--space-24, 24px);
					padding: 0 var(--spacing-xs) 0 var(--space-10);
					border-color: var(--neutral-300);
				}

				.display-text {
					font-size: var(--font-size-small);
				}
			}
		}

		.detail {
			display: flex;
			padding: 6px 0;
			justify-content: space-between;
			align-items: baseline;
			border-bottom: var(--border-width-thin) solid var(--neutral-200);

			&:last-child {
				border-bottom: none;
			}

			.label {
				flex: 0 0 auto;
				white-space: nowrap;
				color: var(--neutral-600);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-medium);
				line-height: var(--line-height-normal);
			}

			.value {
				flex: 1;
				min-width: 0;
				color: var(--neutral-900);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-regular);
				line-height: var(--line-height-normal);
				text-align: end;
				padding-left: var(--spacing-sm);
			}
		}
	}
}

.shortcuts-tooltip {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xxs);
	padding: var(--spacing-sm) var(--spacing-md);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);

	.shortcut {
		display: flex;
		gap: var(--spacing-xxs);
	}

	dt {
		font-weight: var(--font-weight-bold);
		color: var(--neutral-900);

		&::after {
			content: " -";
		}
	}

	dd {
		color: var(--neutral-600);
	}
}

.loader {
	width: var(--size-icon-lg);
	height: var(--size-icon-lg);
	border: var(--border-width-base) solid var(--neutral-900);
	border-bottom-color: transparent;
	border-radius: var(--border-radius-full);
	display: inline-block;
	animation: rotation 1s linear infinite;
}

@keyframes rotation {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.copy-group {
	display: flex;
	align-items: center;
	gap: var(--spacing-xxs);
}
</style>
